import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { motion, useInView, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { betaSignup, newsletterSignup, BetaSignupData, NewsletterSignupData } from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NetworkEngineContext } from '../components/NetworkEngineContext';

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
const T = {
  teal: '#5EEAD4',
  tealDark: '#14B8A6',
  lavender: '#C4B5FD',
  lavenderDark: '#8B5CF6',
  coral: '#FDA4AF',
  coralDark: '#F43F5E',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

/* ═══════════════════════════════════════════════════════════════
   GLASS CARD
   ═══════════════════════════════════════════════════════════════ */
const GlassCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  heavy?: boolean;
}> = ({ children, className = '', heavy = false }) => (
  <div
    className={`rounded-3xl border shadow-xl transition-shadow duration-300 ${className}`}
    style={{
      background: heavy ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.7)',
      backdropFilter: heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      borderColor: heavy ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12), 0 1px 3px rgba(148,163,184,0.08)',
    }}
  >
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAGNETIC BUTTON
   ═══════════════════════════════════════════════════════════════ */
const MagneticButton: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  href?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
}> = ({ children, className = '', onClick, href, type = 'button', disabled = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 20 });
  const sy = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.12);
    y.set((e.clientY - r.top - r.height / 2) * 0.12);
  };

  const inner = (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ x: sx, y: sy }}
      className={className}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );

  if (href) return <a href={href}>{inner}</a>;
  if (type === 'submit') return <button type="submit" disabled={disabled} className="contents">{inner}</button>;
  return inner;
};

/* ═══════════════════════════════════════════════════════════════
   REVEAL
   ═══════════════════════════════════════════════════════════════ */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
}> = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const initial = {
    opacity: 0,
    ...(direction === 'up' ? { y: 30 } : direction === 'left' ? { x: -30 } : { x: 30 }),
  };

  return (
    <motion.div
      ref={ref}
      initial={initial}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════════════════════════ */
const useCounter = (target: number, dec: number = 0, trigger: boolean = false) => {
  const mv = useMotionValue(0);
  const sp = useSpring(mv, { stiffness: 80, damping: 20 });
  const [val, setVal] = useState('0');

  useEffect(() => {
    if (trigger) mv.set(target);
  }, [trigger, target, mv]);

  useEffect(() => {
    const unsub = sp.on('change', (v: number) => setVal(v.toFixed(dec)));
    return unsub;
  }, [sp, dec]);

  return val;
};

/* ═══════════════════════════════════════════════════════════════
   GRAIN OVERLAY
   ═══════════════════════════════════════════════════════════════ */
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

/* ═══════════════════════════════════════════════════════════════
   ═══════════════════════════════════════════════════════════════
   SCENE-DRIVEN HOME COMPONENT

   Architecture:
   - Each major section is a "Scene" that pins during scroll
   - Internal scroll progress drives phase-based morphing
   - Overlapping transition hints between scenes
   - Every 150-250px triggers visual change
   - NetworkEngine state machine synced to scroll position
   ═══════════════════════════════════════════════════════════════
   ═══════════════════════════════════════════════════════════════ */
const Home: React.FC = () => {
  const engineCtx = useContext(NetworkEngineContext);

  // ─── Form State ───
  const [userType, setUserType] = useState<'driver' | 'rider' | null>(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', areaCode: '+852', phone: '', vehicleBodyType: '',
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  // ─── Scene State (only updates on discrete phase changes) ───
  const [currentFeature, setCurrentFeature] = useState(0);
  const [sliderPos, setSliderPos] = useState(0);
  const [activePhase, setActivePhase] = useState(0);
  const [perspective, setPerspective] = useState<'rider' | 'driver'>('rider');

  // ─── Scene Refs ───
  const featuresSceneRef = useRef<HTMLDivElement>(null);
  const comparisonSceneRef = useRef<HTMLDivElement>(null);
  const valuesSceneRef = useRef<HTMLDivElement>(null);
  const featPhaseRef = useRef(0);
  const valPhaseRef = useRef(0);
  const featOverlapRef = useRef<HTMLDivElement>(null);
  const compOverlapRef = useRef<HTMLDivElement>(null);
  const sliderDragging = useRef(false);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const carFinaleRef  = useRef<HTMLDivElement>(null);
  const inFinaleRef   = useRef(false);   // set true by GSAP onEnter

  // ─── Counter Triggers ───
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });
  const ridesCount = useCounter(500, 0, statsInView);
  const driversCount = useCounter(50, 0, statsInView);
  const ratingCount = useCounter(4.9, 1, statsInView);

  // ─── Phone Formatter ───
  const formatPhone = useCallback((v: string): string => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.length > 4 ? `${d.slice(0, 4)}-${d.slice(4)}` : d;
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // DATA — Updated platform fee to 5%, driver take-home to 95%
  // ═══════════════════════════════════════════════════════════════
  const features = useMemo(() => [
    { num: '01', icon: '⚡', title: 'Instant Match', desc: 'AI-powered spatial indexing connects you with the nearest verified driver in under 10 seconds. Traffic-aware routing and dynamic supply balancing guarantee the fastest pickup.', accent: T.teal, stat: '8s', statLabel: 'avg match time' },
    { num: '02', icon: '📡', title: 'Live Tracking', desc: 'Real-time GPS tracking for every ride. Share your trip with friends, use the emergency SOS button, and enjoy complete peace of mind from pickup to drop-off.', accent: T.lavender, stat: '100%', statLabel: 'rides tracked' },
    { num: '03', icon: '💰', title: 'Fair Pricing', desc: 'See your exact fare before booking. No surge surprises, no hidden fees. Detailed fare breakdown with distance, time, and base rate fully transparent.', accent: T.coral, stat: '$0', statLabel: 'hidden fees' },
    { num: '04', icon: '⭐', title: 'Two-Way Ratings', desc: 'Riders rate drivers. Drivers rate riders. Both sides are accountable, both sides are rewarded. A fair ecosystem where quality rises to the top.', accent: T.teal, stat: '4.9', statLabel: 'avg rating' },
    { num: '05', icon: '🌍', title: 'HK Native', desc: 'Built for Hong Kong streets from the ground up. Gaode Maps integration, Octopus card payments, full bilingual support, and street-level precision across all 18 districts.', accent: T.lavender, stat: '18', statLabel: 'districts' },
  ], []);

  const comparisonMetrics = useMemo(() => [
    { label: 'Platform Fee', traditional: '30%', karvlo: '5%', threshold: 12 },
    { label: 'Driver Take-Home', traditional: '70%', karvlo: '95%', threshold: 25 },
    { label: 'Avg Wait Time', traditional: '8 min', karvlo: '3 min', threshold: 38 },
    { label: 'Fare Transparency', traditional: '40%', karvlo: '100%', threshold: 52 },
    { label: 'Support Response', traditional: '2 hrs', karvlo: '15 min', threshold: 65 },
    { label: 'Surge Pricing', traditional: 'Yes', karvlo: 'No', threshold: 78 },
  ], []);

  const phases = useMemo(() => [
    { icon: '⚡', title: 'Speed & Efficiency', desc: 'AI-powered matching connects you with the nearest driver in under 10 seconds. Spatial indexing and traffic-aware routing make every ride fast.', stat: '8s', statLabel: 'average match time', accent: T.teal },
    { icon: '💎', title: 'Total Transparency', desc: 'See your exact fare before you book. No surge surprises, no hidden fees. Full fare breakdown on every trip.', stat: '0', statLabel: 'hidden fees', accent: T.lavender },
    { icon: '💰', title: 'Fair Driver Earnings', desc: 'Drivers keep 95% of every fare — the highest in the industry. We only take 5% to keep things running. Weekly payouts, performance bonuses, and complete earnings transparency.', stat: '95%', statLabel: 'driver take-home', accent: T.coral },
    { icon: '🛡️', title: 'Trust & Safety', desc: 'Real-time GPS tracking, emergency SOS, verified drivers with background checks, and two-way ratings build accountability for everyone.', stat: '4.9', statLabel: 'avg safety rating', accent: T.teal },
  ], []);

  // ═══════════════════════════════════════════════════════════════
  // ZONE-BASED SCROLL DAMPING
  // ─────────────────────────────────────────────────────────────
  // A virtual scroll position (smoothY) is interpolated toward the
  // real window.scrollY each rAF frame using activeDamping.
  // activeDamping itself is smoothly interpolated toward the target
  // for whichever zone the user is currently in.
  //
  // Zones:
  //   Hero    (0 → heroEnd)           damping 0.12  — natural
  //   Pinned  (heroEnd → sceneEnd)    damping 0.06  — cinematic/heavy
  //   Content (sceneEnd → footerStart)damping 0.12  — natural
  //   Footer  (footerStart → end)     damping 0.20  — quick/light exit
  //   Finale  (car pin)               damping 0.06  — heavy, same as pinned
  //
  // IMPORTANT: native scroll position is NEVER modified.
  // We only change how GSAP's ScrollTrigger.update() reads scroll
  // via the virtual position fed back through a CSS var / transform.
  // Actually: we use a translate3d wrapper approach — the page div
  // inside is translated by (smoothY - window.scrollY) to offset
  // the difference, giving the *appearance* of damped scroll while
  // the browser scrollbar and GSAP both see native scrollY.
  //
  // Wait — that also conflicts with GSAP pins. The cleanest approach
  // that doesn't break pins is: do NOT change what GSAP sees. Just
  // read smoothY for driving any CSS custom properties / effects that
  // we own. Pinned scenes are driven by GSAP natively (correct).
  // For the user, this gives a subtle "swim" feel without breaking
  // any ScrollTrigger-pinned sections.
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

    // Damping constants
    const D_HERO    = 0.12;
    const D_PINNED  = 0.06;
    const D_CONTENT = 0.12;
    const D_FOOTER  = 0.20;
    const D_MOBILE_PINNED = 0.10;
    const D_MOBILE_FOOTER = 0.18;

    let activeDamping = D_HERO;
    let smoothY       = window.scrollY;
    let raf: number;

    // Measure zone boundaries after first paint so GSAP pin spacers
    // are in place and getBoundingClientRect() is accurate.
    let heroEnd     = window.innerHeight;
    let sceneEnd    = heroEnd + window.innerHeight + 1500 + 1200 + 800;
    let footerStart = 0; // resolved below

    const resolveZones = () => {
      heroEnd     = window.innerHeight;
      sceneEnd    = heroEnd + window.innerHeight + 1500 + 1200 + 800;
      const footer = document.querySelector('footer');
      footerStart = footer
        ? document.documentElement.scrollHeight - footer.offsetHeight
        : document.documentElement.scrollHeight - window.innerHeight * 0.6;
    };

    resolveZones();
    window.addEventListener('resize', resolveZones);

    const tick = () => {
      const sy       = window.scrollY;
      const totalH   = document.documentElement.scrollHeight - window.innerHeight;

      // ── Determine target damping for current zone ──
      let targetDamping: number;
      if (sy >= footerStart) {
        targetDamping = isMobile ? D_MOBILE_FOOTER : D_FOOTER;
      } else if (inFinaleRef.current) {
        targetDamping = isMobile ? D_MOBILE_PINNED : D_PINNED;
      } else if (sy > heroEnd && sy < sceneEnd) {
        targetDamping = isMobile ? D_MOBILE_PINNED : D_PINNED;
      } else {
        targetDamping = isMobile ? 0.16 : D_CONTENT;
      }

      // Smooth zone-to-zone transition (≈400–600ms at 60fps)
      activeDamping += (targetDamping - activeDamping) * 0.08;

      // Interpolate virtual scroll position
      smoothY += (sy - smoothY) * activeDamping;

      // Expose smoothed value as a CSS custom property so any
      // CSS-transform effects can optionally read it
      document.documentElement.style.setProperty('--smooth-scroll-y', `${smoothY.toFixed(2)}`);
      document.documentElement.style.setProperty('--scroll-progress', totalH > 0 ? `${(sy / totalH).toFixed(4)}` : '0');

      /* dev logging — remove before prod */
      if (process.env.NODE_ENV === 'development') {
        const zone = sy >= footerStart ? 'FOOTER'
                   : inFinaleRef.current ? 'FINALE'
                   : sy > heroEnd && sy < sceneEnd ? 'PINNED'
                   : 'NORMAL';
        (window as any).__karvloScroll = { zone, damping: activeDamping.toFixed(4) };
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resolveZones);
      document.documentElement.style.removeProperty('--smooth-scroll-y');
      document.documentElement.style.removeProperty('--scroll-progress');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // GSAP SCENE ENGINE
  // Pinned scenes with scroll-driven phase transitions.
  // Each scene: Enter → Development → Peak → Release
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Scene 3: Features — Pinned morphing through 5 features ──
      if (featuresSceneRef.current) {
        ScrollTrigger.create({
          trigger: featuresSceneRef.current,
          start: 'top top',
          end: '+=1500',
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const p = self.progress;
            // Map scroll progress to feature index (5 features)
            const newPhase = Math.min(4, Math.floor(p * 5));
            if (newPhase !== featPhaseRef.current) {
              featPhaseRef.current = newPhase;
              setCurrentFeature(newPhase);
            }
            // Overlap hint for next scene
            if (featOverlapRef.current) {
              featOverlapRef.current.style.opacity = String(
                p > 0.9 ? (p - 0.9) * 10 : 0
              );
            }
          },
        });
      }

      // ── Scene 4: Comparison — Pinned with scroll-driven slider ──
      if (comparisonSceneRef.current) {
        ScrollTrigger.create({
          trigger: comparisonSceneRef.current,
          start: 'top top',
          end: '+=1200',
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            // Only auto-drive slider if user isn't manually dragging
            if (!sliderDragging.current) {
              const p = self.progress;
              // Slider moves from 5% to 95% over first 70% of scroll
              const newPos = p < 0.7
                ? (p / 0.7) * 100
                : 100;
              setSliderPos(Math.round(newPos));
            }
            if (compOverlapRef.current) {
              compOverlapRef.current.style.opacity = String(
                self.progress > 0.9 ? (self.progress - 0.9) * 10 : 0
              );
            }
          },
        });
      }

      // ── Scene 5: Values — Pinned with 4 morphing phases ──
      if (valuesSceneRef.current) {
        ScrollTrigger.create({
          trigger: valuesSceneRef.current,
          start: 'top top',
          end: '+=800',
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const p = self.progress;
            const newPhase = Math.min(3, Math.floor(p * 4));
            if (newPhase !== valPhaseRef.current) {
              valPhaseRef.current = newPhase;
              setActivePhase(newPhase);
            }
          },
        });
      }

      // ── NetworkEngine state machine triggers ──
      ScrollTrigger.create({
        trigger: '#scene-hero',
        start: 'top top',
        end: 'bottom center',
        onEnter: () => engineCtx?.current?.setState('hero_idle'),
        onEnterBack: () => engineCtx?.current?.setState('hero_idle'),
      });

      if (featuresSceneRef.current) {
        ScrollTrigger.create({
          trigger: featuresSceneRef.current,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => engineCtx?.current?.setState('features_active'),
          onEnterBack: () => engineCtx?.current?.setState('features_active'),
        });
      }

      if (comparisonSceneRef.current) {
        ScrollTrigger.create({
          trigger: comparisonSceneRef.current,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => engineCtx?.current?.setState('comparison'),
          onEnterBack: () => engineCtx?.current?.setState('comparison'),
        });
      }

      if (valuesSceneRef.current) {
        ScrollTrigger.create({
          trigger: valuesSceneRef.current,
          start: 'top center',
          end: 'bottom center',
          onEnter: () => engineCtx?.current?.setState('values_morph'),
          onEnterBack: () => engineCtx?.current?.setState('values_morph'),
        });
      }

      ScrollTrigger.create({
        trigger: '#scene-perspectives',
        start: 'top center',
        end: 'bottom center',
        onEnter: () => engineCtx?.current?.setState('perspective_split'),
        onEnterBack: () => engineCtx?.current?.setState('perspective_split'),
      });

      ScrollTrigger.create({
        trigger: '#scene-signup',
        start: 'top center',
        end: 'bottom center',
        onEnter: () => engineCtx?.current?.setState('beta_focus'),
        onEnterBack: () => engineCtx?.current?.setState('beta_focus'),
      });

      // ── Scene 10: Car Finale — long pin so car centres on screen ──
      // 1 400 px runway lets the car complete its full side-view
      // centering animation while the wheel interceptor makes it
      // feel heavy; even a small physical scroll drives the full arc.
      if (carFinaleRef.current) {
        ScrollTrigger.create({
          trigger: carFinaleRef.current,
          start: 'top top',
          end: '+=1400',
          pin: true,
          anticipatePin: 1,
          onEnter:     () => { inFinaleRef.current = true;  },
          onLeave:     () => { inFinaleRef.current = false; },
          onEnterBack: () => { inFinaleRef.current = true;  },
          onLeaveBack: () => { inFinaleRef.current = false; },
        });
      }
    });

    return () => ctx.revert();
  }, [engineCtx]);

  // ═══════════════════════════════════════════════════════════════
  // COMPARISON SLIDER — manual drag override
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!sliderDragging.current || !sliderContainerRef.current) return;
      const rect = sliderContainerRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const pos = ((clientX - rect.left) / rect.width) * 100;
      setSliderPos(Math.max(0, Math.min(100, pos)));
    };
    const handleUp = () => { sliderDragging.current = false; };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // ACCENT SHIFT
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    engineCtx?.current?.setAccentShift(perspective === 'driver' ? 'lavender' : 'teal');
  }, [perspective, engineCtx]);

  // ═══════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleBetaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userType) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const data: BetaSignupData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        areaCode: formData.areaCode,
        phone: formData.phone,
        userType,
        ...(userType === 'driver' && formData.vehicleBodyType ? { vehicleBodyType: formData.vehicleBodyType } : {}),
      };
      await betaSignup(data);
      setSubmitSuccess(true);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit. Please try again.';
      setSubmitError(status === 409 ? 'This email is already registered. Please log in instead.' : msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    setNewsletterError('');
    try {
      await newsletterSignup({ email: newsletterEmail } as NewsletterSignupData);
      setNewsletterSuccess(true);
    } catch (err: any) {
      setNewsletterError(err?.response?.data?.message || 'Failed. Try again.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════
  const kFactor = Math.max(0, Math.min(1, sliderPos / 100));

  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  // RENDER — Scene-Driven Narrative
  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="relative z-10">
      {/* Grain texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-[1] opacity-[0.02]"
        style={{ backgroundImage: GRAIN_SVG, backgroundRepeat: 'repeat' }}
      />

      {/* ══════════════════════════════════════════════════════════
          SCENE 1 — HERO  (Cinematic Intro)
          ─────────────────────────────────────────────────────────
          Layout  : full 100vh, absolutely-layered
          Part 0  : video only (0–0.8 s)
          Part A  : badge (0.8s) → headline (1.3s) → subtitle (1.85s)
          Part B  : buttons + mouse cue pop from bottom (2.4s / 2.85s)
          Part C  : first scroll → Header slides in + ScrollCar fades in

          VIDEO   : place /public/hero-bg.mov (HK city footage)
                    Gradient fallback shown automatically when absent.
         ══════════════════════════════════════════════════════════ */}
      <section
        id="scene-hero"
        className="snap-section relative overflow-hidden"
        style={{ height: '100vh' }}
      >
        {/* ── Layer 0: dark night-city fallback gradient ── */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background:
              'radial-gradient(ellipse at 22% 100%, rgba(20,184,166,0.13) 0%, transparent 55%), ' +
              'radial-gradient(ellipse at 78% 0%,  rgba(139,92,246,0.11) 0%, transparent 55%), ' +
              'linear-gradient(160deg, #020617 0%, #0a0f1e 45%, #0d0924 100%)',
          }}
        />

        {/* ── Layer 1: HK city video — hero-bg in client/public/ ── */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-[1]"
          onError={(e) => {
            // Hide video on decode error — gradient fallback on Layer 0 is visible
            (e.currentTarget as HTMLVideoElement).style.display = 'none';
          }}
        >
          {/* VP9 WebM — Firefox, Chrome, Edge (works where H.264 is unavailable) */}
          <source src="/hero-bg.webm" type="video/webm" />
          {/* H.264 MP4 — Safari, Chrome, Edge fallback */}
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
        <div aria-hidden="true" className="sr-only"><a href="https://www.vecteezy.com/free-videos/hong-kong-street" tabIndex={-1}>Hong Kong Street Stock Videos by Vecteezy</a></div>

        {/* ── Layer 2: cinematic vignette overlay ── */}
        <div
          className="absolute inset-0 z-[2]"
          style={{
            background:
              'linear-gradient(to bottom, rgba(2,6,23,0.38) 0%, rgba(2,6,23,0.15) 35%, rgba(2,6,23,0.52) 70%, rgba(2,6,23,0.90) 100%)',
          }}
        />

        {/* ── Layer 3: content + ambient glows ── */}
        <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center px-4 sm:px-6">
          {/* Ambient glow accents */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute bottom-1/4 left-1/4 w-[480px] h-[480px] rounded-full opacity-[0.07]"
              style={{ background: 'radial-gradient(circle, #5EEAD4, transparent 65%)' }}
            />
            <div
              className="absolute top-1/4 right-1/4 w-72 h-72 rounded-full opacity-[0.06]"
              style={{ background: 'radial-gradient(circle, #C4B5FD, transparent 65%)' }}
            />
          </div>

          <div className="relative max-w-5xl mx-auto text-center">
            {/* ── Part A: Badge ── */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mb-10"
            >
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: T.teal }} />
                <span className="text-white/75 text-sm font-medium tracking-wide">
                  Now in Open Beta &mdash; Hong Kong
                </span>
              </div>
            </motion.div>

            {/* ── Part A: Headline ── */}
            <h1 className="text-5xl sm:text-7xl lg:text-[5.5rem] font-black leading-[0.92] tracking-tight mb-6">
              {/* "Movement," — slides up */}
              <motion.span
                initial={{ opacity: 0, y: 35 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 1.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="block text-white"
              >
                Movement,
              </motion.span>
              {/* "Reimagined." — cursive, sweeps in from left with skew */}
              <motion.span
                initial={{ opacity: 0, x: -32, skewX: -10 }}
                animate={{ opacity: 1, x: 0, skewX: 0 }}
                transition={{ duration: 1.9, delay: 1.75, ease: [0.22, 1, 0.36, 1] }}
                className="block pb-3 bg-gradient-to-r from-teal-400 via-violet-400 to-rose-400 bg-clip-text text-transparent"
                style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700, fontSize: '1.08em', letterSpacing: '-0.01em' }}
              >
                Reimagined.
              </motion.span>
            </h1>

            {/* ── Part A: Subtitle ── */}
            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.85, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg sm:text-xl text-white/65 max-w-xl mx-auto leading-relaxed mb-12"
            >
              Lightning-fast matching. Transparent pricing. Verified drivers.
              The ride experience Hong Kong deserves.
              <br aria-hidden="true" />
              <span className="text-white/45 text-base font-medium">香港專車 · 重新定義出行</span>
            </motion.p>

            {/* ── Part B: CTA buttons — spring pop from below ── */}
            <motion.div
              initial={{ opacity: 0, y: 70 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 3.1, ease: [0.34, 1.56, 0.64, 1] }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <MagneticButton
                href="#scene-signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:bg-white/90 transition-all duration-300 cursor-pointer"
              >
                Join the Beta
              </MagneticButton>
              <MagneticButton
                href="/#scene-signup"
                className="inline-flex items-center justify-center px-8 py-4 rounded-2xl border border-white/30 text-white bg-white/10 backdrop-blur-sm font-semibold text-lg hover:bg-white/20 transition-all duration-300 cursor-pointer"
              >
                Open App &rarr;
              </MagneticButton>
            </motion.div>
          </div>
        </div>

        {/* ── Part B: Scroll mouse cue — spring pop from below ── */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 3.8, ease: [0.34, 1.56, 0.64, 1] }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-[3]"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border-2 border-white/35 rounded-full flex items-start justify-center p-1.5"
          >
            <motion.div className="w-1 h-1 bg-white/55 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 2 — STATS
          Glass cards with animated counters
         ══════════════════════════════════════════════════════════ */}
      <section ref={statsRef} className="snap-section relative min-h-screen flex items-center py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: ridesCount, suffix: '+', label: 'Rides Completed', accent: T.teal, isRating: false },
              { value: driversCount, suffix: '+', label: 'Verified Drivers', accent: T.lavender, isRating: false },
              { value: ratingCount, suffix: '', label: 'Average Rating', accent: T.coral, isRating: true },
            ].map((stat, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <GlassCard className="p-8 text-center">
                  <p className="text-4xl sm:text-5xl font-black text-slate-800 mb-1">
                    {stat.isRating ? stat.value : parseInt(stat.value).toLocaleString()}{stat.suffix}
                  </p>
                  <p className="text-slate-400 font-medium">{stat.label}</p>
                  {stat.isRating && (
                    <div className="flex justify-center gap-1 mt-2">
                      {[1, 2, 3, 4, 5].map(j => (
                        <span key={j} className="text-amber-400 text-lg">★</span>
                      ))}
                    </div>
                  )}
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 3 — FEATURES (PINNED)
          Single morphing container — 5 internal phases.
          Scroll drives which feature is visible.
         ══════════════════════════════════════════════════════════ */}
      <section
        ref={featuresSceneRef}
        className="snap-section relative min-h-screen flex items-center justify-center px-4 sm:px-6"
      >
        <div className="relative z-10 max-w-2xl mx-auto w-full py-20">
          {/* Scene header */}
          <div className="text-center mb-8">
            <p
              className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: T.teal }}
            >
              The System
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 leading-tight mb-4">
              Five pillars.
              <br />
              <span className="text-slate-400">One platform.</span>
            </h2>
            <p className="text-slate-500 text-lg max-w-md mx-auto">
              Each component designed to work in concert — creating a ride experience
              that&apos;s fast, fair, and fundamentally better.
            </p>
          </div>

          {/* Phase progress bar */}
          <div className="flex gap-2 mb-8 max-w-sm mx-auto">
            {features.map((f, i) => (
              <div
                key={i}
                className="h-1.5 flex-1 rounded-full transition-all duration-500"
                style={{
                  background: i <= currentFeature
                    ? features[currentFeature].accent
                    : 'rgba(148,163,184,0.15)',
                  transform: i === currentFeature ? 'scaleY(1.5)' : 'scaleY(1)',
                }}
              />
            ))}
          </div>

          {/* Morphing feature card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentFeature}
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <GlassCard heavy className="p-8 sm:p-12">
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="text-6xl sm:text-7xl font-black opacity-10 select-none"
                    style={{ color: features[currentFeature].accent }}
                  >
                    {features[currentFeature].num}
                  </span>
                  <span className="text-4xl sm:text-5xl">{features[currentFeature].icon}</span>
                </div>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-800 mb-4">
                  {features[currentFeature].title}
                </h3>
                <p className="text-slate-500 text-base sm:text-lg leading-relaxed mb-8">
                  {features[currentFeature].desc}
                </p>

                <div
                  className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
                  style={{ background: `${features[currentFeature].accent}18` }}
                >
                  <span
                    className="text-xl sm:text-2xl font-black"
                    style={{ color: features[currentFeature].accent }}
                  >
                    {features[currentFeature].stat}
                  </span>
                  <span className="text-slate-500 text-sm font-medium">
                    {features[currentFeature].statLabel}
                  </span>
                </div>
              </GlassCard>
            </motion.div>
          </AnimatePresence>

          {/* Feature selector dots (also clickable) */}
          <div className="flex justify-center gap-3 mt-8">
            {features.map((f, i) => (
              <button
                key={i}
                onClick={() => setCurrentFeature(i)}
                className="group flex flex-col items-center gap-1"
              >
                <div
                  className="w-3 h-3 rounded-full transition-all duration-300"
                  style={{
                    background: i === currentFeature ? f.accent : 'rgba(148,163,184,0.2)',
                    transform: i === currentFeature ? 'scale(1.4)' : 'scale(1)',
                    boxShadow: i === currentFeature ? `0 0 12px ${f.accent}60` : 'none',
                  }}
                />
                <span className="text-[10px] text-slate-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {f.num}
                </span>
              </button>
            ))}
          </div>

          {/* Overlap hint → next scene */}
          <div
            ref={featOverlapRef}
            className="absolute bottom-8 left-0 right-0 text-center"
            style={{ opacity: 0, pointerEvents: 'none' }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: T.coral }}>
              ↓ The Difference
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 4 — INTERACTIVE COMPARISON (PINNED)
          Full-bleed image slider: competitor mockup vs Karvlo.
          Divider sweeps left→right revealing data rows beneath.
         ══════════════════════════════════════════════════════════ */}
      <section
        ref={comparisonSceneRef}
        className="snap-section relative min-h-screen flex items-center py-20 px-4 sm:px-6"
      >
        <div className="max-w-5xl mx-auto w-full">
          <div className="text-center mb-10">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.coral }}>
              The Difference
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight mb-3">
              See the shift.
            </h2>
            <p className="text-slate-500 text-lg max-w-lg mx-auto">
              Scroll to reveal — or drag the divider.
            </p>
          </div>

          {/* ── Full-bleed image slider ── */}
          <div
            ref={sliderContainerRef}
            className="relative rounded-3xl overflow-hidden select-none cursor-ew-resize"
            style={{ touchAction: 'none', aspectRatio: '16/7', minHeight: 340 }}
            onMouseDown={() => { sliderDragging.current = true; }}
            onTouchStart={() => { sliderDragging.current = true; }}
          >
            {/* ── LEFT panel: Competitors ── full width behind clip ── */}
            <div className="absolute inset-0">
              {/* Messy dark competitor UI mockup */}
              <svg viewBox="0 0 800 350" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
                <rect width="800" height="350" fill="#111827"/>
                {/* Noisy gradient */}
                <defs>
                  <linearGradient id="cgBg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1f2937"/>
                    <stop offset="100%" stopColor="#0f172a"/>
                  </linearGradient>
                  <linearGradient id="cgSurge" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444"/>
                    <stop offset="100%" stopColor="#dc2626"/>
                  </linearGradient>
                </defs>
                <rect width="800" height="350" fill="url(#cgBg)"/>
                {/* Top bar */}
                <rect x="0" y="0" width="800" height="52" fill="#1f2937"/>
                <rect x="16" y="16" width="64" height="20" rx="4" fill="#374151"/>
                <text x="88" y="30" fontFamily="sans-serif" fontSize="13" fill="#9ca3af">Uber · Lyft · Grab · Bolt</text>
                {/* Surge badge */}
                <rect x="660" y="12" width="124" height="28" rx="6" fill="url(#cgSurge)"/>
                <text x="673" y="30" fontFamily="sans-serif" fontSize="11" fontWeight="700" fill="white">⚡ 2.4× SURGE</text>
                {/* Map area */}
                <rect x="0" y="52" width="800" height="178" fill="#1a2332"/>
                {/* Grid lines */}
                {[0,1,2,3,4,5,6].map(i => (
                  <line key={i} x1={i*130} y1="52" x2={i*130} y2="230" stroke="#243040" strokeWidth="1"/>
                ))}
                {[0,1,2,3].map(i => (
                  <line key={i} x1="0" y1={52+i*44} x2="800" y2={52+i*44} stroke="#243040" strokeWidth="1"/>
                ))}
                {/* Roads */}
                <rect x="0" y="138" width="800" height="8" fill="#2d3f52" opacity="0.9"/>
                <rect x="260" y="52" width="8" height="178" fill="#2d3f52" opacity="0.9"/>
                {/* Car icons (competitor) */}
                <rect x="120" y="112" width="24" height="36" rx="5" fill="#374151"/>
                <circle cx="132" cy="153" r="5" fill="#1f2937"/>
                <rect x="320" y="80" width="24" height="36" rx="5" fill="#374151"/>
                <circle cx="332" cy="121" r="5" fill="#1f2937"/>
                <rect x="490" y="155" width="24" height="36" rx="5" fill="#374151"/>
                <circle cx="502" cy="196" r="5" fill="#1f2937"/>
                {/* Surge heat overlay */}
                <ellipse cx="400" cy="140" rx="200" ry="90" fill="#ef4444" opacity="0.08"/>
                <ellipse cx="200" cy="100" rx="120" ry="60" fill="#f97316" opacity="0.06"/>
                {/* Pin */}
                <circle cx="400" cy="130" r="14" fill="#ef4444" opacity="0.9"/>
                <circle cx="400" cy="130" r="6" fill="white"/>
                {/* Bottom panel */}
                <rect x="0" y="230" width="800" height="120" fill="#1f2937"/>
                <rect x="16" y="244" width="200" height="14" rx="3" fill="#374151"/>
                <rect x="16" y="266" width="140" height="10" rx="3" fill="#2d3748"/>
                {/* Hidden fee rows */}
                {/* Fare breakdown: shape blocks + readable text */}
                <rect x="16" y="282" width="78" height="11" rx="2" fill="#4b5563" opacity="0.55"/>
                <rect x="168" y="282" width="50" height="11" rx="2" fill="#4b5563" opacity="0.4"/>
                <rect x="16" y="299" width="54" height="11" rx="2" fill="#991b1b" opacity="0.5"/>
                <text x="76" y="310" fontFamily="sans-serif" fontSize="12" fill="#ef4444" fontWeight="800">+ $11.20 surge</text>
                <rect x="16" y="315" width="64" height="11" rx="2" fill="#4b5563" opacity="0.4"/>
                <rect x="168" y="315" width="40" height="11" rx="2" fill="#4b5563" opacity="0.35"/>
                <line x1="16" y1="331" x2="264" y2="331" stroke="#374151" strokeWidth="1"/>
                <text x="16" y="347" fontFamily="sans-serif" fontSize="13" fontWeight="800" fill="#ef4444">Total:  $21.60</text>
                {/* Book button */}
                <rect x="580" y="258" width="200" height="44" rx="8" fill="#374151"/>
                <text x="680" y="284" textAnchor="middle" fontFamily="sans-serif" fontSize="13" fontWeight="600" fill="#9ca3af">Book Now</text>
                {/* Labels */}
                <text x="400" y="70" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="700" fill="#6b7280" opacity="0.7">TRADITIONAL RIDESHARE</text>
              </svg>
              {/* Dark tinted overlay on competitor side */}
              <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.18)' }}/>
              {/* Label */}
              <div className="absolute top-4 left-5 flex items-center gap-2 z-10">
                <div className="px-3 py-1 rounded-lg text-xs font-bold text-white/80 tracking-widest uppercase" style={{ background: 'rgba(239,68,68,0.75)', backdropFilter: 'blur(8px)' }}>
                  Competitors
                </div>
              </div>
            </div>

            {/* ── RIGHT panel: Karvlo ── clipped to right of divider ── */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
            >
              {/* Clean Karvlo UI mockup */}
              <svg viewBox="0 0 800 350" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
                <defs>
                  <linearGradient id="kgBg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#f0fdfa"/>
                    <stop offset="100%" stopColor="#f8fafc"/>
                  </linearGradient>
                  <linearGradient id="kgBtn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14B8A6"/>
                    <stop offset="100%" stopColor="#0d9488"/>
                  </linearGradient>
                  <linearGradient id="kgRoute" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#5EEAD4"/>
                    <stop offset="100%" stopColor="#C4B5FD"/>
                  </linearGradient>
                </defs>
                <rect width="800" height="350" fill="url(#kgBg)"/>
                {/* Top bar */}
                <rect x="0" y="0" width="800" height="52" fill="rgba(255,255,255,0.95)"/>
                <rect x="16" y="14" width="44" height="24" rx="6" fill="#5EEAD4" opacity="0.3"/>
                <text x="22" y="30" fontFamily="sans-serif" fontSize="12" fontWeight="700" fill="#0d9488">karvlo</text>
                <text x="88" y="30" fontFamily="sans-serif" fontSize="12" fill="#64748b">Clean. Honest. Yours.</text>
                {/* No surge badge */}
                <rect x="650" y="12" width="134" height="28" rx="6" fill="#f0fdfa" stroke="#5EEAD4" strokeWidth="1.5"/>
                <text x="717" y="30" textAnchor="middle" fontFamily="sans-serif" fontSize="11" fontWeight="700" fill="#0d9488">✓ No Surge Ever</text>
                {/* Map area */}
                <rect x="0" y="52" width="800" height="178" fill="#f8fafc"/>
                {/* Soft grid */}
                {[0,1,2,3,4,5,6].map(i => (
                  <line key={i} x1={i*130} y1="52" x2={i*130} y2="230" stroke="#e2e8f0" strokeWidth="1"/>
                ))}
                {[0,1,2,3].map(i => (
                  <line key={i} x1="0" y1={52+i*44} x2="800" y2={52+i*44} stroke="#e2e8f0" strokeWidth="1"/>
                ))}
                {/* Roads */}
                <rect x="0" y="138" width="800" height="8" fill="#cbd5e1" opacity="0.7"/>
                <rect x="260" y="52" width="8" height="178" fill="#cbd5e1" opacity="0.7"/>
                {/* Animated route */}
                <path d="M160 200 Q300 130 460 120 Q560 115 640 100" stroke="url(#kgRoute)" strokeWidth="4" fill="none" strokeDasharray="8 4" opacity="0.9"/>
                {/* Karvlo car */}
                <rect x="450" y="104" width="26" height="38" rx="6" fill="#14B8A6"/>
                <rect x="455" y="109" width="16" height="10" rx="3" fill="#f0fdfa" opacity="0.8"/>
                <circle cx="456" cy="144" r="5" fill="#0f172a"/>
                <circle cx="470" cy="144" r="5" fill="#0f172a"/>
                {/* Pickup/dropoff */}
                <circle cx="160" cy="198" r="10" fill="#14B8A6" opacity="0.9"/>
                <circle cx="160" cy="198" r="5" fill="white"/>
                <circle cx="640" cy="98" r="10" fill="#8B5CF6" opacity="0.9"/>
                <circle cx="640" cy="98" r="5" fill="white"/>
                {/* Bottom panel */}
                <rect x="0" y="230" width="800" height="120" fill="rgba(255,255,255,0.98)"/>
                <text x="16" y="252" fontFamily="sans-serif" fontSize="14" fontWeight="700" fill="#1e293b">3 min away · 5 star driver</text>
                <text x="16" y="270" fontFamily="sans-serif" fontSize="11" fill="#64748b">Raymond L. · Toyota Camry · HK-1234</text>
                {/* Clean fare breakdown — readable sizes */}
                <text x="16" y="293" fontFamily="sans-serif" fontSize="11" fill="#64748b">Base fare</text>
                <text x="130" y="293" fontFamily="sans-serif" fontSize="11" fill="#475569">$8.00</text>
                <text x="16" y="310" fontFamily="sans-serif" fontSize="11" fill="#64748b">Platform fee</text>
                <text x="130" y="310" fontFamily="sans-serif" fontSize="11" fill="#14B8A6" fontWeight="700">$0.40  (5%)</text>
                <line x1="16" y1="318" x2="264" y2="318" stroke="#e2e8f0" strokeWidth="1"/>
                <text x="16" y="332" fontFamily="sans-serif" fontSize="13" fontWeight="800" fill="#1e293b">Total  $8.40</text>
                <rect x="16" y="337" width="214" height="14" rx="3" fill="#ccfbf1"/>
                <text x="24" y="348" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#0d9488">Driver earns 95%  ·  $7.60</text>
                {/* Book button */}
                <rect x="580" y="252" width="200" height="48" rx="12" fill="url(#kgBtn)"/>
                <text x="680" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="700" fill="white">Book Ride</text>
                <text x="800" y="270" textAnchor="middle" fontFamily="sans-serif" fontSize="9" fill="#94a3b8">KARVLO</text>
              </svg>
              {/* Label */}
              <div className="absolute top-4 right-5 flex items-center gap-2 z-10">
                <div className="px-3 py-1 rounded-lg text-xs font-bold tracking-widest uppercase" style={{ background: 'rgba(20,184,166,0.85)', backdropFilter: 'blur(8px)', color: 'white' }}>
                  Karvlo
                </div>
              </div>
            </div>

            {/* ── Metrics overlay — slides in from right as divider moves ── */}
            <div
              className="absolute bottom-0 left-0 right-0 pointer-events-none"
              style={{
                background: 'linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.0) 100%)',
                height: '44%',
              }}
            />

            {/* ── Draggable divider line ── */}
            <div
              className="absolute top-0 bottom-0 z-20"
              style={{
                left: `${sliderPos}%`,
                transform: 'translateX(-50%)',
                width: 3,
                background: `linear-gradient(180deg, ${T.teal}, ${T.lavender}, ${T.coral})`,
                boxShadow: `0 0 20px ${T.teal}60, 0 0 50px ${T.teal}20`,
              }}
            >
              {/* Handle */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full shadow-2xl flex items-center justify-center cursor-ew-resize"
                style={{
                  background: 'rgba(255,255,255,0.96)',
                  backdropFilter: 'blur(16px)',
                  border: `2px solid ${T.teal}`,
                  boxShadow: `0 4px 24px rgba(0,0,0,0.12), 0 0 0 4px ${T.teal}20`,
                }}
              >
                <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
                  <path d="M1 7h18M6 2L1 7l5 5M14 2l5 5-5 5" stroke={T.tealDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Outer border */}
            <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{ border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 40px rgba(148,163,184,0.18)' }}/>
          </div>

          {/* ── Data rows beneath — revealed as divider moves ── */}
          <div
            className="relative mt-3 rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.6)',
            }}
          >
            {/* Left competitor header + right karvlo header — absolutely positioned */}
            <div className="grid grid-cols-2 border-b border-slate-100/80">
              <div className="px-6 py-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-400 inline-block"/>
                <span className="text-xs font-bold tracking-widest uppercase text-slate-400">Competitors</span>
              </div>
              <div className="px-6 py-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: T.teal }}/>
                <span className="text-xs font-bold tracking-widest uppercase" style={{ color: T.tealDark }}>Karvlo</span>
              </div>
            </div>
            {comparisonMetrics.map((m, i) => {
              const revealed = sliderPos > m.threshold;
              return (
                <div
                  key={i}
                  className="grid grid-cols-2 border-b border-slate-100/60 last:border-0 transition-all duration-500"
                >
                  {/* Competitor value */}
                  <div
                    className="px-6 py-3 flex items-center justify-between transition-all duration-500"
                    style={{ opacity: revealed ? 0.35 : 1 }}
                  >
                    <span className="text-xs text-slate-400 font-medium">{m.label}</span>
                    <span className="text-base font-black text-slate-400 tabular-nums">{m.traditional}</span>
                  </div>
                  {/* Karvlo value — slides in with divider */}
                  <div
                    className="px-6 py-3 flex items-center justify-between transition-all duration-500 overflow-hidden"
                    style={{
                      background: revealed ? `${T.teal}0d` : 'transparent',
                      opacity: revealed ? 1 : 0,
                      transform: revealed ? 'translateX(0)' : 'translateX(16px)',
                    }}
                  >
                    <span className="text-xs text-slate-500 font-medium">{m.label}</span>
                    <span
                      className="text-base font-black tabular-nums transition-all duration-500"
                      style={{
                        color: T.tealDark,
                        textShadow: revealed ? `0 0 16px ${T.teal}40` : 'none',
                      }}
                    >
                      {m.karvlo}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Karvlo advantage callout */}
          <motion.div
            className="mt-6 text-center"
            animate={{ opacity: kFactor > 0.8 ? 1 : 0, y: kFactor > 0.8 ? 0 : 10 }}
            transition={{ duration: 0.5 }}
          >
            <GlassCard className="inline-flex items-center gap-3 px-6 py-3">
              <span className="text-xl">🏆</span>
              <span className="text-slate-600 font-medium">
                Only <strong className="font-black" style={{ color: T.teal }}>5% platform fee</strong> — drivers keep <strong className="font-black" style={{ color: T.teal }}>95%</strong>
              </span>
            </GlassCard>
          </motion.div>

          {/* Overlap hint */}
          <div
            ref={compOverlapRef}
            className="text-center mt-6"
            style={{ opacity: 0, pointerEvents: 'none' }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: T.lavender }}>
              ↓ Why Karvlo
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 5 — WHY CHOOSE US (PINNED)
          Morphing container — 4 phases driven by scroll.
          Each phase shifts icon, title, description, stat.
         ══════════════════════════════════════════════════════════ */}
      <section
        ref={valuesSceneRef}
        className="snap-section relative min-h-screen flex items-center py-20 px-4 sm:px-6"
      >
        <div className="max-w-5xl mx-auto w-full">
          <div className="text-center mb-12">
            <p
              className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: T.lavender }}
            >
              Why Karvlo
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight">
              Built on
              <br />
              <span className="text-slate-400">better principles.</span>
            </h2>
          </div>

          {/* Phase selector (also clickable) */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {phases.map((phase, i) => (
              <button
                key={i}
                onClick={() => setActivePhase(i)}
                className={`relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activePhase === i
                    ? 'text-slate-800'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {activePhase === i && (
                  <motion.div
                    layoutId="phaseSelector"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: `${phase.accent}15`,
                      border: `1px solid ${phase.accent}30`,
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <span>{phase.icon}</span>
                  <span className="hidden sm:inline">{phase.title}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Morphing content card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activePhase}
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -15, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <GlassCard heavy className="max-w-3xl mx-auto p-10 sm:p-14">
                <div className="flex flex-col sm:flex-row items-start gap-8">
                  <div
                    className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
                    style={{ background: `${phases[activePhase].accent}15` }}
                  >
                    {phases[activePhase].icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-800 mb-4">
                      {phases[activePhase].title}
                    </h3>
                    <p className="text-slate-500 text-lg leading-relaxed mb-6">
                      {phases[activePhase].desc}
                    </p>
                    <div
                      className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
                      style={{ background: `${phases[activePhase].accent}15` }}
                    >
                      <span
                        className="text-2xl font-black"
                        style={{ color: phases[activePhase].accent }}
                      >
                        {phases[activePhase].stat}
                      </span>
                      <span className="text-slate-500 text-sm font-medium">
                        {phases[activePhase].statLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Phase dots */}
                <div className="flex justify-center gap-2 mt-10">
                  {phases.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePhase(i)}
                      className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                      style={{
                        background: i === activePhase ? p.accent : 'rgba(148,163,184,0.2)',
                        transform: i === activePhase ? 'scale(1.3)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 6 — PERSPECTIVES (Rider / Driver)
         ══════════════════════════════════════════════════════════ */}
      <section id="scene-perspectives" className="snap-section relative min-h-screen flex items-center py-28 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-16">
            <p
              className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: T.lavender }}
            >
              Two Perspectives
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight mb-8">
              One platform.
              <br />
              <span className="text-slate-400">Two experiences.</span>
            </h2>

            <GlassCard className="inline-flex p-1.5">
              {(['rider', 'driver'] as const).map(view => (
                <button
                  key={view}
                  onClick={() => setPerspective(view)}
                  className={`relative px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    perspective === view ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {perspective === view && (
                    <motion.div
                      layoutId="perspectiveToggle"
                      className="absolute inset-0 rounded-xl"
                      style={{
                        background: view === 'rider'
                          ? 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(196,181,253,0.08))'
                          : 'linear-gradient(135deg, rgba(196,181,253,0.15), rgba(253,164,175,0.08))',
                        border: `1px solid ${view === 'rider' ? 'rgba(94,234,212,0.3)' : 'rgba(196,181,253,0.3)'}`,
                      }}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 capitalize">{view}</span>
                </button>
              ))}
            </GlassCard>
          </Reveal>

          <AnimatePresence mode="wait">
            <motion.div
              key={perspective}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.4 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(perspective === 'rider' ? [
                  { icon: '📱', title: 'Book in Seconds', desc: 'Set pickup, choose tier, confirm. Three taps maximum.', stat: '3 taps', accent: T.teal },
                  { icon: '📡', title: 'Live Tracking', desc: 'Watch your driver approach in real-time on the map.', stat: 'Real-time', accent: T.teal },
                  { icon: '💳', title: 'Easy Payment', desc: 'Automatic payment. Split fares with friends.', stat: '1-tap pay', accent: T.teal },
                ] : [
                  { icon: '💵', title: 'Higher Earnings', desc: 'Keep 95% of every fare. Weekly payouts, zero hidden fees.', stat: '95%', accent: T.lavender },
                  { icon: '📊', title: 'Analytics Dashboard', desc: 'Track earnings, ratings, hotspots, and peak hours.', stat: 'Real-time', accent: T.lavender },
                  { icon: '🏆', title: 'Rewards & Bonuses', desc: 'Milestones, streaks, peak multipliers, and loyalty tiers.', stat: 'Gamified', accent: T.lavender },
                ]).map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <GlassCard className="p-7 hover:shadow-2xl transition-shadow duration-300 h-full">
                      <span className="text-3xl mb-4 block">{card.icon}</span>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">{card.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed mb-4">{card.desc}</p>
                      <span
                        className="text-xs font-bold uppercase tracking-wider"
                        style={{ color: card.accent }}
                      >
                        {card.stat}
                      </span>
                    </GlassCard>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 7 — TESTIMONIALS
         ══════════════════════════════════════════════════════════ */}
      <section className="snap-section relative min-h-screen flex items-center py-28 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-16">
            <p
              className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: T.coral }}
            >
              Testimonials
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight">
              Loved by riders
              <br />
              <span className="text-slate-400">and drivers alike.</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Sarah L.', role: 'Rider — Central', text: "Karvlo is the smoothest ride app I've used in HK. The pricing transparency is a game-changer.", color: T.teal },
              { name: 'Michael C.', role: 'Driver — Kowloon', text: "Finally, a platform that respects drivers. Higher earnings and a beautiful dashboard to track everything.", color: T.lavender },
              { name: 'Emily W.', role: 'Rider — Wan Chai', text: "The two-way rating system and in-app chat make every ride feel safe and personal.", color: T.coral },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <GlassCard className="p-7 h-full">
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map(j => (
                      <span key={j} className="text-amber-400 text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6 italic">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ background: t.color }}
                    >
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{t.name}</p>
                      <p className="text-xs text-slate-400">{t.role}</p>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 8 — BETA SIGNUP
          Staggered reveals for form elements.
         ══════════════════════════════════════════════════════════ */}
      <section id="scene-signup" className="snap-section relative flex items-center py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-12">
            <p
              className="text-sm font-bold tracking-[0.2em] uppercase mb-4"
              style={{ color: T.teal }}
            >
              Join Beta
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight mb-4">
              Be part of the
              <br />
              <span className="bg-gradient-to-r from-teal-400 via-violet-400 to-rose-400 bg-clip-text text-transparent">
                future of riding.
              </span>
            </h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">
              Sign up for early access. Riders and drivers get onboarding priority.
            </p>
          </Reveal>

          {submitSuccess ? (
            <Reveal>
              <GlassCard heavy className="p-12 text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: T.teal }}>
                  You&apos;re in!
                </h3>
                <p className="text-slate-500">
                  Welcome to the Karvlo beta. Check your email for next steps.
                </p>
              </GlassCard>
            </Reveal>
          ) : (
            <Reveal>
              {/* User type toggle */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                {[
                  { type: 'rider' as const, icon: '🚶', label: 'I need rides' },
                  { type: 'driver' as const, icon: '🚗', label: 'I want to drive' },
                ].map(({ type, icon, label }) => (
                  <button key={type} onClick={() => setUserType(type)} className="contents">
                    <GlassCard
                      heavy={userType === type}
                      className={`flex items-center gap-3 px-6 py-4 cursor-pointer transition-all duration-300 ${
                        userType === type
                          ? 'ring-2 ring-teal-300/50'
                          : 'hover:shadow-lg'
                      }`}
                    >
                      <span className="text-2xl">{icon}</span>
                      <span
                        className={`font-semibold ${
                          userType === type ? 'text-slate-800' : 'text-slate-500'
                        }`}
                      >
                        {label}
                      </span>
                    </GlassCard>
                  </button>
                ))}
              </div>

              {userType && (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={handleBetaSubmit}
                >
                  <GlassCard heavy className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                        required
                        className="px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all"
                        style={{ backdropFilter: 'blur(8px)' }}
                      />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                        required
                        className="px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all"
                        style={{ backdropFilter: 'blur(8px)' }}
                      />
                    </div>

                    <input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all mb-4"
                      style={{ backdropFilter: 'blur(8px)' }}
                    />

                    <div className="flex gap-2 mb-4">
                      <div className="w-32">
                        <SearchableDropdown
                          name="areaCode"
                          value={formData.areaCode}
                          onChange={e => setFormData({ ...formData, areaCode: e.target.value })}
                          options={[
                            { value: '+852', label: '+852 (HK)' },
                            { value: '+86', label: '+86 (CN)' },
                            { value: '+1', label: '+1 (US)' },
                            { value: '+44', label: '+44 (UK)' },
                            { value: '+81', label: '+81 (JP)' },
                            { value: '+65', label: '+65 (SG)' },
                          ]}
                          placeholder="+852"
                        />
                      </div>
                      <input
                        type="tel"
                        placeholder="1234-5678"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                        required
                        className="flex-1 px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all"
                        style={{ backdropFilter: 'blur(8px)' }}
                      />
                    </div>

                    {userType === 'driver' && (
                      <div className="mb-4">
                        <SearchableDropdown
                          name="vehicleBodyType"
                          value={formData.vehicleBodyType}
                          onChange={e => setFormData({ ...formData, vehicleBodyType: e.target.value })}
                          /*
                           * Values must exist in the Driver schema's
                           * vehicleBodyType enum (server/src/models/Driver.ts).
                           * 'minibus', 'luxury' and 'ev' did not, so a driver
                           * choosing any of those three failed Mongoose
                           * validation and could not sign up at all.
                           */
                          options={[
                            { value: 'sedan', label: 'Sedan' },
                            { value: 'suv', label: 'SUV' },
                            { value: 'hatchback', label: 'Hatchback' },
                            { value: 'mpv', label: 'MPV / Van' },
                            { value: 'commercial-van', label: 'Minibus / Commercial Van' },
                            { value: 'luxury-sedan', label: 'Luxury Sedan' },
                            { value: 'electric', label: 'Electric Vehicle' },
                          ]}
                          placeholder="Select Vehicle Type"
                        />
                      </div>
                    )}

                    {submitError && (
                      <div
                        className="px-4 py-3 rounded-xl text-sm mb-4 border text-rose-600"
                        style={{
                          background: 'rgba(255,241,242,0.8)',
                          borderColor: 'rgba(254,205,211,0.5)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        {submitError}
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          required
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 accent-teal-600 flex-shrink-0"
                        />
                        <span className="text-sm text-slate-500 leading-relaxed text-left">
                          I agree to the{' '}
                          <a href="/terms" className="underline hover:text-teal-600 transition-colors">Terms of Service</a>
                          {' '}and{' '}
                          <a href="/privacy" className="underline hover:text-teal-600 transition-colors">Privacy Policy</a>
                          . I understand my data will be handled in accordance with Hong Kong PDPO.
                        </span>
                      </label>
                    </div>

                    <MagneticButton
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 bg-slate-800 text-white font-bold text-lg rounded-2xl shadow-lg shadow-slate-300/40 hover:shadow-xl hover:bg-slate-700 transition-all duration-300 text-center cursor-pointer ${
                        isSubmitting ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      {isSubmitting
                        ? 'Signing up...'
                        : `Join as ${userType === 'rider' ? 'Rider' : 'Driver'}`}
                    </MagneticButton>
                  </GlassCard>
                </motion.form>
              )}
            </Reveal>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SCENE 9 — NEWSLETTER
         ══════════════════════════════════════════════════════════ */}
      <section className="snap-section relative flex items-center py-32 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center w-full">
          <Reveal>
            <div className="mb-8">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                viewport={{ once: true }}
                className="inline-block mb-4 px-4 py-2 rounded-full text-sm font-semibold"
                style={{
                  background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(147, 51, 234, 0.1))',
                  color: T.tealDark,
                  border: `1px solid rgba(20, 184, 166, 0.2)`,
                }}
              >
                ✨ Stay Connected
              </motion.div>
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-slate-800 via-teal-700 to-purple-700 bg-clip-text text-transparent mb-6">
                Join the Karvlo Movement
              </h2>
              <p className="text-slate-600 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
                Get exclusive updates on new features, city launches, driver promotions, and platform improvements — delivered straight to your inbox.
              </p>
            </div>

            {newsletterSuccess ? (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-3 px-8 py-5 rounded-2xl font-semibold shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(240, 253, 250, 0.95), rgba(236, 254, 255, 0.95))',
                  borderColor: 'rgba(94, 234, 212, 0.4)',
                  color: T.tealDark,
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 10px 40px rgba(20, 184, 166, 0.15)',
                }}
              >
                <span className="text-2xl">✅</span>
                <span className="text-lg">You&apos;re subscribed! Check your inbox for a confirmation email.</span>
              </motion.div>
            ) : (
              <form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto"
              >
                <div className="flex-1">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={newsletterEmail}
                    onChange={e => setNewsletterEmail(e.target.value)}
                    required
                    className="w-full px-6 py-5 rounded-2xl bg-white border-2 border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-200/30 transition-all shadow-sm text-lg font-medium"
                    style={{ backdropFilter: 'blur(8px)' }}
                  />
                </div>
                <MagneticButton
                  type="submit"
                  disabled={newsletterSubmitting}
                  className={`px-10 py-5 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-bold text-lg rounded-2xl shadow-xl shadow-teal-600/30 hover:shadow-2xl hover:from-teal-500 hover:to-teal-600 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    newsletterSubmitting ? 'opacity-60 pointer-events-none' : ''
                  }`}
                >
                  {newsletterSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Subscribing...
                    </span>
                  ) : (
                    'Subscribe Now'
                  )}
                </MagneticButton>
              </form>
            )}
            {newsletterError && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-rose-600 text-sm font-medium mt-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 inline-block"
              >
                {newsletterError}
              </motion.p>
            )}
          </Reveal>
        </div>
      </section>

    </div>
  );
};

export default Home;
